import { parseNumberInput } from "@/lib/parse-number";
import { cleanFloat, roundTo } from "@/lib/tool-kit/grades/rounding";

/**
 * UK honours degree classification from module marks.
 *
 * Each year's mark is the credit-weighted average of its modules; the degree average is
 * the year marks combined by the year weights. Boundaries: First 70+, upper second (2:1)
 * 60–69, lower second (2:2) 50–59, Third 40–49 — the conventional bands, as modelled in
 * UKSCQA (UUK, GuildHE and QAA), "Degree classification: transparent, consistent and
 * fair academic standards" (November 2018), Annexe D.
 *
 * The same report surveys the variations this models as options: year weightings from
 * even to 100% final year (it tests 50/50, 40/60, 33/67, 30/70, 25/75 and 20/80),
 * discounting ("best 90 / best 100 credits per level"), rounding at 0.5, and zones of
 * consideration "within one or two percentage points from the grade boundary", often
 * with preponderance. The Office for Students (England), "Bachelors' degree
 * classification algorithms", OfS 2025.74 (6 November 2025), questions discounting and
 * best-of-several-algorithms rules and asks providers keeping them from September 2026
 * to say so — so these rules are in flux. Every university's own regulations decide.
 */

export interface UkModule {
  credits: number;
  mark: number;
}

/** Year weightings for a three-year degree, as the select offers them. */
export const UK_WEIGHT_PRESETS = {
  "0-1-2": [0, 1, 2],
  "0-40-60": [0, 40, 60],
  "0-50-50": [0, 50, 50],
  "0-30-70": [0, 30, 70],
  "0-25-75": [0, 25, 75],
  "0-20-80": [0, 20, 80],
  "0-0-100": [0, 0, 100],
} as const;

/** A starting point for a four-year integrated master's, not any university's rule. */
export const UK_INTEGRATED_WEIGHTS = [0, 20, 40, 40] as const;

/** Typed rows with real credits and a mark from 0 to 100; half-typed rows are skipped. */
export function readModules(
  rows: readonly { credits: string; mark: string }[]
): UkModule[] {
  const modules: UkModule[] = [];
  for (const row of rows) {
    const credits = parseNumberInput(row.credits);
    const mark = parseNumberInput(row.mark);
    if (credits === null || mark === null) {
      continue;
    }
    if (credits > 0 && mark >= 0 && mark <= 100) {
      modules.push({ credits, mark });
    }
  }
  return modules;
}

export type UkClass =
  | "first"
  | "upper-second"
  | "lower-second"
  | "third"
  | "below-third";

export const UK_BOUNDARIES: readonly { cls: UkClass; min: number }[] = [
  { cls: "first", min: 70 },
  { cls: "upper-second", min: 60 },
  { cls: "lower-second", min: 50 },
  { cls: "third", min: 40 },
];

/**
 * Drop up to `credits` of the lowest-marked credit, splitting a module when the
 * allowance ends partway through it ("best 100 of 120 credits").
 */
export function discountLowest(
  modules: readonly UkModule[],
  credits: number
): UkModule[] {
  let remaining = Math.max(0, credits);
  const ascending = [...modules].sort((a, b) => a.mark - b.mark);
  const kept: UkModule[] = [];
  for (const entry of ascending) {
    const dropped = Math.min(entry.credits, remaining);
    remaining -= dropped;
    if (entry.credits - dropped > 0) {
      kept.push({ credits: entry.credits - dropped, mark: entry.mark });
    }
  }
  return kept;
}

export function yearAverage(
  modules: readonly UkModule[]
): { average: number; credits: number } | null {
  let credits = 0;
  let total = 0;
  for (const entry of modules) {
    credits += entry.credits;
    total += entry.credits * entry.mark;
  }
  if (credits <= 0) {
    return null;
  }
  return { average: cleanFloat(total / credits), credits: cleanFloat(credits) };
}

export function classify(average: number): UkClass {
  const value = cleanFloat(average);
  for (const boundary of UK_BOUNDARIES) {
    if (value >= boundary.min) {
      return boundary.cls;
    }
  }
  return "below-third";
}

/** The class above `average` when it sits within `zone` marks below that boundary. */
export function borderlineFor(
  average: number,
  zone: number
): { cls: UkClass; boundary: number; gap: number } | null {
  if (zone <= 0) {
    return null;
  }
  for (const boundary of UK_BOUNDARIES) {
    const gap = cleanFloat(boundary.min - average);
    if (gap > 0 && gap <= zone) {
      return { cls: boundary.cls, boundary: boundary.min, gap };
    }
  }
  return null;
}

export interface UkYearInput {
  modules: readonly UkModule[];
  weight: number;
}

export interface UkYearResult {
  weight: number;
  average: number | null;
  /** Credits used after discounting. */
  credits: number;
}

export interface UkDegreeResult {
  years: UkYearResult[];
  /** The weighted degree average before any rounding. */
  average: number;
  /** The figure classified: rounded to a whole mark when rounding is on. */
  classifiedAverage: number;
  cls: UkClass;
  borderline: { cls: UkClass; boundary: number; gap: number } | null;
  /** Counted credits (weighted years only) at or above the next boundary up. */
  preponderance: { boundary: number; credits: number; total: number } | null;
}

export interface UkDegreeOptions {
  discountCredits: number;
  roundToWhole: boolean;
  zone: number;
}

function countedModules(year: UkYearInput, discount: number): UkModule[] {
  return discount > 0
    ? discountLowest(year.modules, discount)
    : [...year.modules];
}

function preponderanceFor(
  years: readonly UkYearInput[],
  discount: number,
  boundary: number
) {
  let credits = 0;
  let total = 0;
  for (const year of years) {
    if (year.weight <= 0) {
      continue;
    }
    for (const entry of countedModules(year, discount)) {
      total += entry.credits;
      if (entry.mark >= boundary) {
        credits += entry.credits;
      }
    }
  }
  return { boundary, credits: cleanFloat(credits), total: cleanFloat(total) };
}

/**
 * The classification, or a list of the weighted years still missing marks (1-based).
 */
export function classifyDegree(
  years: readonly UkYearInput[],
  options: UkDegreeOptions
): { result: UkDegreeResult } | { missing: number[] } {
  const missing: number[] = [];
  const results: UkYearResult[] = [];
  let weighted = 0;
  let weightTotal = 0;
  for (const [index, year] of years.entries()) {
    const summary = yearAverage(countedModules(year, options.discountCredits));
    results.push({
      weight: year.weight,
      average: summary?.average ?? null,
      credits: summary?.credits ?? 0,
    });
    if (year.weight <= 0) {
      continue;
    }
    if (summary === null) {
      missing.push(index + 1);
      continue;
    }
    weighted += year.weight * summary.average;
    weightTotal += year.weight;
  }
  if (missing.length > 0 || weightTotal <= 0) {
    return { missing };
  }
  const average = cleanFloat(weighted / weightTotal);
  const classifiedAverage = options.roundToWhole
    ? roundTo(average, 0)
    : average;
  const borderline = borderlineFor(classifiedAverage, options.zone);
  return {
    result: {
      years: results,
      average,
      classifiedAverage,
      cls: classify(classifiedAverage),
      borderline,
      preponderance: borderline
        ? preponderanceFor(years, options.discountCredits, borderline.boundary)
        : null,
    },
  };
}
